import React, { useState } from 'react';
import { QRDesign } from '@/types/qr';
import { BatchItem } from '@/types/history';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { getCompositedCanvas } from '@/lib/qr/export';
import JSZip from 'jszip';
import {
  Upload,
  Download,
  FileSpreadsheet,
  Trash2,
  CheckCircle2,
  FileArchive,
  Plus,
  Sparkles
} from 'lucide-react';
import confetti from 'canvas-confetti';

interface BatchGeneratorProps {
  design: QRDesign;
}

const EXAMPLE_ITEMS: BatchItem[] = [
  { id: 'example-1', rowNumber: 1, title: 'Google Search', type: 'url', rawText: 'https://google.com', status: 'valid' },
  { id: 'example-2', rowNumber: 2, title: 'GitHub', type: 'url', rawText: 'https://github.com', status: 'valid' },
  { id: 'example-3', rowNumber: 3, title: 'Wikipedia', type: 'url', rawText: 'https://wikipedia.org', status: 'valid' }
];

export const BatchGenerator: React.FC<BatchGeneratorProps> = ({ design }) => {
  const { showToast } = useToast();
  const [items, setItems] = useState<BatchItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newRowTitle, setNewRowTitle] = useState('');
  const [newRowText, setNewRowText] = useState('');

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) return;

      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      const parsedItems: BatchItem[] = [];

      lines.forEach((line, idx) => {
        if (idx === 0 && (line.toLowerCase().includes('url') || line.toLowerCase().includes('name'))) {
          return;
        }

        const parts = line.split(',');
        let title = `Item ${idx + 1}`;
        let rawText = '';

        if (parts.length >= 2) {
          title = parts[0].replace(/^["']|["']$/g, '').trim();
          rawText = parts.slice(1).join(',').replace(/^["']|["']$/g, '').trim();
        } else {
          rawText = parts[0].replace(/^["']|["']$/g, '').trim();
          title = rawText.length > 20 ? rawText.substring(0, 20) + '...' : rawText;
        }

        if (rawText) {
          parsedItems.push({
            id: `${Date.now()}-${idx}`,
            rowNumber: parsedItems.length + 1,
            title,
            type: 'url',
            rawText,
            status: 'valid'
          });
        }
      });

      if (parsedItems.length > 0) {
        setItems(parsedItems);
        showToast('success', 'CSV Loaded', `Imported ${parsedItems.length} rows successfully.`);
      } else {
        showToast('warning', 'Empty CSV', 'No valid rows were found in the uploaded file.');
      }
    };
    reader.readAsText(file);
  };

  const handleAddRow = () => {
    if (!newRowText.trim()) return;
    const newItem: BatchItem = {
      id: `${Date.now()}-${Math.random()}`,
      rowNumber: items.length + 1,
      title: newRowTitle.trim() || `Row ${items.length + 1}`,
      type: 'url',
      rawText: newRowText.trim(),
      status: 'valid'
    };
    setItems([...items, newItem]);
    setNewRowTitle('');
    setNewRowText('');
  };

  const handleRemoveRow = (id: string) => {
    setItems(items.filter((i) => i.id !== id));
  };

  const handleClearAll = () => {
    setItems([]);
  };

  const handleLoadExample = () => {
    setItems(EXAMPLE_ITEMS);
  };

  const handleDownloadSampleCSV = () => {
    const sample = 'Title,Content\nGoogle Website,https://google.com\nCompany Wi-Fi,WIFI:T:WPA;S:MyCoffeeWiFi;P:SecretPass123;;\nSupport Email,mailto:support@example.com\nPayee UPI,upi://pay?pa=merchant@upi&pn=Merchant%20Store';
    const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'qr-studio-batch-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportZip = async () => {
    if (items.length === 0) return;

    try {
      setIsProcessing(true);
      const zip = new JSZip();
      const folder = zip.folder('qr-studio-codes');

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const canvas = await getCompositedCanvas(item.rawText, design, 1024);
        const dataUrl = canvas.toDataURL('image/png');
        const base64Data = dataUrl.replace(/^data:image\/png;base64,/, '');
        const safeTitle = (item.title || `qr-${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');

        folder?.file(`${String(i + 1).padStart(3, '0')}_${safeTitle}.png`, base64Data, {
          base64: true
        });
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-studio-batch-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      setIsProcessing(false);
      confetti({ particleCount: 40, spread: 60, origin: { y: 0.7 } });
      showToast('success', 'Batch ZIP Created', `Packed ${items.length} QR codes into ZIP.`);
    } catch (err) {
      setIsProcessing(false);
      console.error(err);
      showToast('error', 'Batch Export Failed', 'Error creating ZIP package.');
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Header Store Utility Card */}
      <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-[21px] font-semibold text-foreground tracking-tight flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              Batch QR Code Generator
            </h3>
            <p className="text-[14px] text-muted-foreground mt-0.5">
              Generate hundreds of styled QR codes from CSV or spreadsheets in seconds (100% client-side)
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {items.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadExample}
                leftIcon={<Sparkles className="h-3.5 w-3.5" />}
              >
                Try an Example
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadSampleCSV}
              leftIcon={<Download className="h-3.5 w-3.5" />}
            >
              Sample CSV
            </Button>

            <label className="inline-flex">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {}}
                leftIcon={<Upload className="h-3.5 w-3.5" />}
                className="cursor-pointer"
              >
                Upload CSV
              </Button>
            </label>
          </div>
        </div>

        {/* Manual Add Row Strip */}
        <div className="p-3.5 rounded-xl bg-muted border border-border flex flex-col sm:flex-row gap-2.5 sm:items-end">
          <div className="w-full sm:w-1/3">
            <Input
              placeholder="Label / Title (e.g. Table 1)"
              value={newRowTitle}
              onChange={(e) => setNewRowTitle(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-1/2">
            <Input
              placeholder="URL / Payload (e.g. https://restaurant.com/menu/1)"
              value={newRowText}
              onChange={(e) => setNewRowText(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddRow}
            leftIcon={<Plus className="h-4 w-4" />}
            className="w-full sm:w-auto"
          >
            Add Row
          </Button>
        </div>
      </div>

      {/* Batch Table List Card */}
      <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
        <div className="flex justify-between items-center pb-2 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold tracking-tight text-foreground">
              Queued Items ({items.length})
            </span>
          </div>

          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-[12px] text-destructive font-normal hover:underline cursor-pointer"
              >
                Clear Queue
              </button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground space-y-2">
            <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="text-[14px] font-medium">No items in batch queue</p>
            <p className="text-[12px] max-w-sm mx-auto">
              Upload a CSV file or add rows above to generate batch QR codes.
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 rounded-xl bg-muted border border-border text-[14px] transition-all"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="font-mono text-[12px] font-normal text-muted-foreground w-6">
                    #{idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">
                      {item.title}
                    </p>
                    <p className="font-mono text-[12px] text-muted-foreground truncate">
                      {item.rawText}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <span className="inline-flex items-center gap-1 text-[12px] font-normal text-success">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Ready
                  </span>
                  <button
                    onClick={() => handleRemoveRow(item.id)}
                    className="p-1.5 text-muted-foreground hover:text-destructive rounded-full transition-colors cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Batch Export Footer */}
        {items.length > 0 && (
          <div className="pt-4 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-3">
            <span className="text-[14px] text-muted-foreground">
              Ready to generate {items.length} HD QR codes with active styling.
            </span>
            <Button
              variant="primary"
              size="lg"
              onClick={handleExportZip}
              isLoading={isProcessing}
              leftIcon={<FileArchive className="h-5 w-5" />}
              className="w-full sm:w-auto"
            >
              Export All as .ZIP Archive
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
